import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Play, Phone, CheckCircle2, Zap, Shield, MapPin } from "lucide-react";
import { useDemoPopup } from "@/pages/LandingPage";
import { useSettings } from "@/hooks/useSettings";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export const Hero = ({ metierOverride }) => {
  const { openDemoPopup } = useDemoPopup();
  const { settings } = useSettings();
  const [abHook, setAbHook] = useState(null);

  // Load A/B hook variant if on a metier page
  useEffect(() => {
    if (!metierOverride) return;
    const metierKey = Object.entries(metierOverride).length > 0 ? metierOverride.title?.toLowerCase() : null;
    if (!metierKey) return;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/admin/config/ab-hooks/public/${metierKey}`);
        const hooks = res.data.hooks || [];
        if (hooks.length > 0) {
          const picked = hooks[Math.floor(Math.random() * hooks.length)];
          setAbHook(picked);
          axios.post(`${API_BASE}/api/admin/config/ab-hooks/track`, { metier: metierKey, variant_id: picked.variant_id, event: "view" });
        }
      } catch { /* */ }
    })();
  }, [metierOverride]);

  const m = abHook ? { ...metierOverride, hook: abHook.hook, subtitle: abHook.subtitle || metierOverride?.subtitle } : metierOverride;

  return (
    <section id="hero" className="relative pt-20 lg:pt-32 pb-12 lg:pb-24 overflow-hidden blueprint-bg">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5"></div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="overflow-hidden">
            {/* Trust Badge */}
            <div className="inline-flex items-center gap-1.5 bg-primary/10 border border-primary/20 text-primary px-3 py-1.5 rounded-md text-xs sm:text-sm font-semibold mb-4 sm:mb-6">
              <Shield className="w-3.5 h-3.5 flex-shrink-0" />
              <span style={{ fontFamily: 'IBM Plex Sans, sans-serif', letterSpacing: '0.1em' }} className="uppercase text-[0.65rem] sm:text-xs">IA Spécialisée Artisanat Suisse</span>
            </div>

            {/* Ville Badge */}
            {m?.ville && (
              <div className="flex items-center gap-2 mb-3">
                <div className="inline-flex items-center gap-1.5 bg-accent/10 border border-accent/20 text-accent px-3 py-1.5 rounded-md text-xs font-semibold">
                  <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="uppercase tracking-wider text-[0.65rem]">{m.ville.nom} — {m.ville.canton}</span>
                </div>
              </div>
            )}

            {/* Main Heading */}
            <h1 className="text-2xl sm:text-4xl md:text-4xl lg:text-5xl xl:text-6xl font-black leading-[1.15] text-slate-900 mb-3 sm:mb-6 tracking-tight">
              {m ? m.hook : (<>On vous trouve des{" "}<span className="text-accent" style={{ fontSize: 'inherit' }}>nouveaux clients</span>{" "}pendant que vous êtes sur chantier</>)}
            </h1>

            {/* Subheading */}
            <p className="text-sm sm:text-base lg:text-xl text-slate-600 leading-relaxed mb-3 sm:mb-5 max-w-xl">
              {m ? m.subtitle : (<><strong className="text-slate-900">80% des artisans manquent de clients.</strong>{" "}Notre IA vous en trouve, répond à votre place et relance vos devis — automatiquement.</>)}
            </p>

            {/* City context line */}
            {m?.ville && (
              <p className="text-xs sm:text-sm text-primary font-medium mb-4 max-w-xl leading-relaxed">
                {m.ville.contexte}
              </p>
            )}

            {/* Specialites tags when on metier page */}
            {m?.metierSpecialites && (
              <div className="flex flex-wrap gap-1.5 mb-4">
                {m.metierSpecialites.map((s) => (
                  <span key={s} className="text-[0.65rem] sm:text-xs font-semibold bg-accent/10 text-accent border border-accent/20 px-2.5 py-1 rounded-md">{s}</span>
                ))}
              </div>
            )}

            {/* 3 Axes Preview */}
            <div className="flex flex-col gap-1.5 mb-5 sm:mb-8">
              {(m?.metierProblemes ? [
                { text: `Acquisition de clients ${m?.ville ? `à ${m.ville.nom}` : "via Google & Meta Ads"}` },
                { text: "Call bot qui répond quand vous êtes occupé" },
                { text: "Relances devis & factures automatiques" },
              ] : [
                { text: "Acquisition de nouveaux clients via Google & Meta Ads" },
                { text: "Call bot qui répond quand vous êtes occupé" },
                { text: "Relances devis & factures automatiques" },
              ]).map((item) => (
                <div key={item.text} className="flex items-center gap-1.5 text-slate-700">
                  <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-accent flex-shrink-0" />
                  <span className="font-medium text-xs sm:text-sm">{item.text}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col gap-2.5 lg:flex-row lg:gap-3 mb-4 sm:mb-6">
              <Button
                onClick={openDemoPopup}
                size="lg"
                className="bg-accent hover:bg-orange-600 text-white shadow-lg font-bold text-sm sm:text-base px-5 sm:px-6 py-4 sm:py-5 rounded-md transition-all hover:scale-105 hover:shadow-xl"
                data-testid="hero-cta-call"
              >
                <Phone className="mr-1.5 sm:mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                Recevoir une démo gratuite
                <ArrowRight className="ml-1.5 sm:ml-2 w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
              <Button
                onClick={() => document.getElementById('solution')?.scrollIntoView({ behavior: 'smooth' })}
                size="lg"
                variant="outline"
                className="border-2 border-primary text-primary hover:bg-primary hover:text-white font-semibold text-sm sm:text-base px-5 sm:px-6 py-4 sm:py-5 rounded-md transition-all"
                data-testid="hero-cta-demo"
              >
                <Play className="mr-1.5 sm:mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                Découvrir nos solutions
              </Button>
            </div>

            {/* Trust Note */}
            <p className="text-xs sm:text-sm text-slate-500">
              <strong className="text-slate-700">+30% de transformation</strong> en moyenne chez nos clients artisans
            </p>
          </div>

          {/* Right Content - Hero Image */}
          <div className="relative hidden lg:block">
            <div className="relative">
              <div className="rounded-md overflow-hidden border-4 border-white shadow-2xl">
                <img 
                  src={m?.metierImage || "https://images.unsplash.com/photo-1627664196442-0cec8aa5d5eb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHw0fHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXIlMjBzZXJpb3VzJTIwcG9ydHJhaXQlMjBvcmFuZ2UlMjBibHVlfGVufDB8fHx8MTc3NjI1NTc2MHww&ixlib=rb-4.1.0&q=85"} 
                  alt={m?.metierImageAlt || "Artisan professionnel"} 
                  className="w-full h-[480px] object-cover"
                />
              </div>

              {/* City image overlay when on a ville page */}
              {m?.ville && (
                <div className="absolute -bottom-8 -left-8 w-44 h-32 rounded-md overflow-hidden border-4 border-white shadow-xl">
                  <img
                    src={m.ville.image}
                    alt={m.ville.imageAlt}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-2">
                    <span className="text-white text-xs font-bold">{m.ville.nom}</span>
                  </div>
                </div>
              )}
              
              {/* Floating Stat Card */}
              <div className={`absolute ${m?.ville ? '-bottom-8 right-4' : '-bottom-6 -left-6'} bg-white border-2 border-slate-200 rounded-md shadow-lg p-5`} style={{boxShadow: '4px 4px 0px rgba(30, 90, 158, 0.15)'}}>
                <p className="text-sm text-slate-500 font-medium uppercase tracking-wide">
                  {m?.stats?.label ? `${m.stats.label}` : "Nouveaux clients / mois"}
                </p>
                <p className="text-3xl font-black text-accent">{m?.stats?.clients || "+12"}</p>
              </div>
              
              {/* Floating Badge */}
              <div className="absolute -top-4 -right-4 bg-primary text-white rounded-md shadow-lg px-4 py-3 flex items-center gap-2">
                <Zap className="w-5 h-5 text-accent" />
                <span className="font-bold">IA Active 24/7</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4 mt-10 lg:mt-20">
          {[
            { value: "80", label: "Artisans en manque de clients", suffix: "%" },
            { value: "+30", label: "Transformation", suffix: "%" },
            { value: "24/7", label: "Call bot actif", suffix: "" },
            { value: "0", label: "Devis oublié", suffix: "" },
          ].map((stat) => (
            <div key={stat.label} className="text-center p-3 sm:p-5 bg-white border-2 border-slate-200 rounded-md" style={{boxShadow: '3px 3px 0px rgba(30, 90, 158, 0.1)'}}>
              <p className="stat-number text-primary">{stat.value}<span className="text-xs sm:text-lg">{stat.suffix}</span></p>
              <p className="text-xs sm:text-sm text-slate-500 font-medium mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
